// src/lib/highlightCardText.tsx
// カタルタワード（カード語）を回答本文中でハイライトする共通ユーティリティ。
// 2026-07-12・CEO指示：/sessions・/posts・ルーム結果・LogArea（保存済みセッションカード／拡大モーダル）
// の5箇所でハイライト表現がバラバラだった（丸チップ＋色違い×複数パターン）ため統一する目的で追加。
//
// スタイルは src/app/rooms/[roomId]/RoomView.tsx の highlightCardTexts() に既にあった
// 「文中の単語をその場で黄色ハイライトする」表現をそのまま踏襲している（Kazuto承認済みの見た目）。
// 独立した丸チップにすると一文として読みづらくなる（2026-07-12・Kazuto実機確認）ため、
// あえて別コンポーネント化はせず、文字列の中に単語を探して置き換えるだけのシンプルな関数にしている。
// RoomView.tsx側の実装はリスクを避けるため触っていない（同じロジックの重複はこの1箇所のみ）。

import type { CSSProperties, ReactNode } from "react";

const HIGHLIGHT_STYLE: CSSProperties = {
  background: "rgb(253,230,138)",
  padding: "0 2px",
  borderRadius: 3,
  fontWeight: 600,
};

/**
 * text の中に cardTexts の語が現れる箇所を黄色ハイライトする。
 * text 中に見つからない語（本文が引用符やカードの言い換え等で始まらないケース）は、
 * 文の先頭に同じスタイルで添えてから本文を続ける（情報は必ず見せるが、独立行にはしない）。
 */
export function highlightCardWords(text: string, cardTexts: string[]): ReactNode {
  const words = cardTexts.filter((w): w is string => !!w);
  if (words.length === 0) return text;

  const sorted = [...words].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    `(${sorted.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "g"
  );

  const found = new Set<string>();
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    found.add(match[1]);
    parts.push(
      <span key={`hl-${match.index}`} style={HIGHLIGHT_STYLE}>
        {match[1]}
      </span>
    );
    last = match.index + match[1].length;
  }
  if (last < text.length) parts.push(text.slice(last));

  const unfound = words.filter((w) => !found.has(w));

  if (unfound.length > 0) {
    return (
      <>
        {unfound.map((w, i) => (
          <span key={`hl-unfound-${i}`} style={HIGHLIGHT_STYLE}>
            {w}
          </span>
        ))}{" "}
        {parts.length > 0 ? parts : text}
      </>
    );
  }

  return parts.length > 0 ? parts : text;
}
